/**
 * Lists what a programme still needs, one row per blocker, each with a link to fix it.
 *
 * The submission pack and the governance surface each drew their own list of
 * the same blockers, with different icons, different link copy and only one
 * saying when a section was closed.
 *
 * @module components/shared/ReadinessBlockerList
 */

import React from "react";

import {
  Accordion,
  AccordionItem,
  Badge,
  EmptyState,
  Icon,
  LinkButton,
  ListRow,
  Text,
} from "@/components/ui";

export interface ReadinessBlockerEntry {
  key: string;
  /**
   * A stable name for the row's own testid. The position is not one: the list
   * shrinks as blockers clear, so an index names a different requirement from
   * one read to the next. Falls back to the position when absent.
   */
  id?: string;
  message: string;
  /** The section's own key, for a caller that gathers rows by section. */
  sectionKey?: string;
  /** The section it is cleared in, as its tab names it. */
  section?: string;
  /** Where it is cleared. Absent when the reader has nowhere to go. */
  href?: string;
  /** Why the section exists but is closed to the reader. Ignored with an `href`. */
  lockedReason?: string;
  /** How many modules the row stands for, when it gathers several. */
  count?: number;
  /** The blockers a gathered row stands for, one per module, behind a disclosure. */
  details?: readonly ReadinessBlockerEntry[];
}

/** The way to a blocker's section, or why it is closed, or nothing. */
function BlockerAction({
  entry,
  testId = undefined,
}: {
  entry: ReadinessBlockerEntry;
  testId?: (kind: "link" | "locked") => string;
}): React.ReactNode {
  if (entry.href) {
    return (
      <LinkButton
        to={entry.href}
        variant="outline"
        size="sm"
        icon="arrow-right"
        data-testid={testId?.("link")}
      >
        Open {entry.section ?? "section"}
      </LinkButton>
    );
  }
  if (entry.lockedReason) {
    return (
      <Text
        as="span"
        size="sm"
        tone="muted"
        className="inline-flex items-center gap-1"
        data-testid={testId?.("locked")}
      >
        <Icon name="lock-key" aria-hidden />
        {entry.lockedReason}
      </Text>
    );
  }
  return null;
}

export interface ReadinessBlockerListProps {
  entries: readonly ReadinessBlockerEntry[];
  /** Prefixes `-link-<id>` and `-locked-<id>` on each row's action. */
  testIdPrefix?: string;
  /** Level of each row's disclosure heading, one below the heading above the list. */
  headingLevel?: 2 | 3 | 4 | 5;
  "data-testid"?: string;
}

export function ReadinessBlockerList({
  entries,
  testIdPrefix = undefined,
  headingLevel = 4,
  "data-testid": testId,
}: ReadinessBlockerListProps): React.ReactElement {
  if (entries.length === 0) {
    return (
      <EmptyState
        title="Nothing outstanding"
        description="All requirements at this level have been met."
        data-testid={testIdPrefix ? `${testIdPrefix}-empty` : testId}
      />
    );
  }

  return (
    <ul className="divide-y divide-base-300" data-testid={testId}>
      {entries.map((entry, index) => {
        // One requirement can hold several blockers, so a repeat is numbered
        // rather than given the first row's testids.
        const baseId = entry.id ?? String(index);
        const repeat = entries.slice(0, index).filter((earlier) => earlier.id === entry.id).length;
        const rowId = entry.id && repeat > 0 ? `${baseId}-${repeat + 1}` : baseId;
        const rowTestId = testIdPrefix
          ? (kind: "link" | "locked") => `${testIdPrefix}-${kind}-${rowId}`
          : undefined;
        return (
          <li key={entry.key}>
            <ListRow
              framed={false}
              tone="none"
              leading={<Icon name="circle-dashed" className="mt-0.5 text-muted" aria-hidden />}
              action={
                entry.href
                  ? {
                      label: `Open ${entry.section ?? "section"}`,
                      href: entry.href,
                      "data-testid": rowTestId?.("link"),
                    }
                  : undefined
              }
              trailing={entry.href ? undefined : <BlockerAction entry={entry} testId={rowTestId} />}
            >
              <span className="block">
                {entry.message}
                {entry.count ? (
                  <Badge tone="warning" variant="soft" size="sm" className="ms-2">
                    {entry.count}
                  </Badge>
                ) : null}
              </span>
              {entry.section ? (
                <Text as="span" size="sm" tone="muted" className="block">
                  {entry.section}
                </Text>
              ) : null}
            </ListRow>
            {entry.details?.length ? (
              <Accordion id={`${testIdPrefix ?? "blockers"}-${rowId}-details`} className="mb-2">
                <AccordionItem
                  eventKey="details"
                  headingLevel={headingLevel}
                  title="Show each module"
                >
                  <ul className="divide-y divide-base-300">
                    {entry.details.map((detail) => (
                      <li
                        key={detail.key}
                        className="flex flex-wrap items-center justify-between gap-3 py-1"
                      >
                        <span className="min-w-0 flex-1 basis-64">{detail.message}</span>
                        <BlockerAction entry={detail} />
                      </li>
                    ))}
                  </ul>
                </AccordionItem>
              </Accordion>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
